import { useState, type ReactNode } from 'react'
import { QrCode, Trash } from '@phosphor-icons/react'
import { useFormat, useI18n } from '@/i18n'
import { cn } from '@/lib/cn'
import { Badge, Button, IconButton, ScannerSheet, type ScanMeta, type ScanValidator } from '@/ui'
import { availability, type BagView, type StoreState } from '../projection'

/** Big scan button that keeps the camera open between bags (continuous). */
export function ScanButton({
  label,
  title,
  validate,
  onScan,
  disabled,
  variant = 'primary',
  continuous = true,
}: {
  label: ReactNode
  title?: ReactNode
  validate?: ScanValidator
  onScan: (code: string, meta: ScanMeta) => void
  disabled?: boolean
  variant?: 'primary' | 'secondary' | 'accent'
  continuous?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        size="xl"
        variant={variant}
        fullWidth
        disabled={disabled}
        leadingIcon={<QrCode aria-hidden="true" />}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <ScannerSheet
        open={open}
        onClose={() => setOpen(false)}
        onScan={onScan}
        validate={validate}
        title={title ?? label}
        continuous={continuous}
      />
    </>
  )
}

/** Hold / ordinary / availability / sync badges for one bag (F5.6, from cache + sync results). */
export function BagBadges({ state, bag, now }: { state: StoreState; bag: BagView; now: number }) {
  const { t } = useI18n()
  const lot = state.lots.get(bag.unit_id)
  const a = availability(state, bag, now)
  return (
    <span className="flex flex-wrap gap-1">
      {a === 'ON_HOLD' && (
        <Badge tone="warn">
          {t('store.badge.hold')}
          {lot?.hold_reason ? ` · ${lot.hold_reason.replace(/_/g, ' ')}` : ''}
        </Badge>
      )}
      {a === 'ORDINARY' && <Badge tone="danger">{t('store.badge.ordinary')}</Badge>}
      {a === 'AVAILABLE' && <Badge tone="ok">{t('store.badge.available')}</Badge>}
      {a === 'IN_TRANSIT' && <Badge tone="info">{t('store.badge.inTransit')}</Badge>}
      {a === 'NOT_AVAILABLE' && <Badge>{t('store.badge.notAvailable')}</Badge>}
      {bag.red_tie_pending && <Badge tone="danger">{t('store.badge.redTie')}</Badge>}
      {bag.pending_command_id && <Badge tone="info">{t('store.badge.notSynced')}</Badge>}
      {bag.sync_exception_ids.length > 0 && (
        <Badge tone="warn">{t('store.badge.heldAtOffice')}</Badge>
      )}
      {bag.sync_error && <Badge tone="danger">{t('store.badge.syncProblem')}</Badge>}
    </span>
  )
}

/** One bag in a list: tag, lot, weight, badges, optional remove. */
export function BagLine({
  state,
  bag,
  now,
  extra,
  onRemove,
}: {
  state: StoreState
  bag: BagView
  now: number
  extra?: ReactNode
  onRemove?: () => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const lot = state.lots.get(bag.unit_id)
  const net = bag.net_g_current ?? bag.net_g_gate
  return (
    <li className="flex items-center gap-3 border-b border-rule/70 py-2.5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-mono text-[15px] font-medium">{bag.qr_payload}</span>
          <span className="font-mono text-[12px] text-muted">{lot?.code ?? '—'}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-muted">
          {net !== null && net !== undefined && <span className="tabular">{fmt.weight(net)}</span>}
          <BagBadges state={state} bag={bag} now={now} />
        </div>
        {extra}
      </div>
      {onRemove && (
        <IconButton size="touch" label={t('store.removeBag')} icon={<Trash />} onClick={onRemove} />
      )}
    </li>
  )
}

/** A big "n of m" counter. */
export function Counter({
  label,
  value,
  total,
  tone = 'neutral',
}: {
  label: ReactNode
  value: number
  total?: number
  tone?: 'neutral' | 'ok' | 'warn'
}) {
  return (
    <div
      className={cn(
        'flex min-w-[96px] flex-1 flex-col rounded-card border px-3 py-2',
        tone === 'ok' && 'border-ok/40 bg-ok-wash',
        tone === 'warn' && 'border-warn/40 bg-warn-wash',
        tone === 'neutral' && 'border-rule bg-surface',
      )}
    >
      <span className="text-[12px] text-muted">{label}</span>
      <span className="whitespace-nowrap font-mono text-[26px] font-bold leading-tight tabular">
        {value}
        {total !== undefined && <span className="text-[16px] text-muted"> / {total}</span>}
      </span>
    </div>
  )
}

/** Yes / No pair with big targets (toggle fields in the spec). */
export function YesNo({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  const { t } = useI18n()
  return (
    <div className="grid grid-cols-2 gap-2" role="radiogroup">
      {([true, false] as const).map((v) => (
        <button
          key={String(v)}
          type="button"
          role="radio"
          aria-checked={value === v}
          disabled={disabled}
          onClick={() => onChange(v)}
          className={cn(
            'min-h-touch rounded-chip border font-mono text-[14px] font-bold uppercase tracking-label transition-colors disabled:opacity-40',
            value === v
              ? v
                ? 'border-ok bg-ok text-white'
                : 'border-danger bg-danger text-white'
              : 'border-ink/40 bg-surface text-ink',
          )}
        >
          {v ? t('store.yes') : t('store.no')}
        </button>
      ))}
    </div>
  )
}
